import {
  haversineMeters,
  interpolateAlongLine,
  bearingBetween,
} from './routeGeo'

export interface CameraContext {
  coords: [number, number][]
  cumDists: number[]
  totalDistM: number
  waypoints: { longitude: number; latitude: number }[]
}

export interface CameraStep {
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
  cursorPos: [number, number]
}

function zoomForSegmentDist(segDist: number): number {
  if (segDist < 500) return 15.5
  if (segDist < 2_000) return 14.5
  if (segDist < 8_000) return 13.0
  if (segDist < 25_000) return 11.5
  if (segDist < 80_000) return 10.0
  return 8.5
}

function pitchForZoom(zoom: number): number {
  return 38 + ((zoom - 8.5) / 7) * 27
}

/**
 * Pure camera computation for a normalized progress value (0..1).
 * Used by the live in-app 3D player to ease between frames along the route.
 */
export function computeCameraStep(progress: number, ctx: CameraContext): CameraStep {
  const t = Math.max(0, Math.min(1, progress))
  const { coords, cumDists, totalDistM, waypoints } = ctx

  const pos = interpolateAlongLine(coords, cumDists, t)

  const lookAheadT = Math.min(t + 0.01, 1)
  const ahead = interpolateAlongLine(coords, cumDists, lookAheadT)
  const bearing =
    t < 0.999
      ? bearingBetween(pos, ahead)
      : bearingBetween(interpolateAlongLine(coords, cumDists, Math.max(0, t - 0.01)), pos)

  const target = t * totalDistM
  let lo = 0
  for (let i = 1; i < cumDists.length; i++) {
    if (cumDists[i] >= target) {
      lo = i - 1
      break
    }
  }
  const segDist = cumDists[Math.min(lo + 1, cumDists.length - 1)] - cumDists[lo]
  let zoom = zoomForSegmentDist(segDist)

  for (const wp of waypoints) {
    const dist = haversineMeters(pos, [wp.longitude, wp.latitude])
    if (dist < 800) {
      zoom = Math.min(zoom + 2, 17)
      break
    }
  }

  return {
    center: pos,
    zoom,
    pitch: pitchForZoom(zoom),
    bearing,
    cursorPos: pos,
  }
}
